import { UseQueryOptions, useMutation, useQuery } from 'react-query';
import {
  createDesignationGroupService,
  createDesignationService,
  deleteDesignationGroupByIdService,
  getDesignationGroupsService,
  getDesignationsService,
  toggleDesignationStatusService,
  updateDesignationService
} from 'services/designations';
import {
  Designation,
  DesignationGroup,
  IGetDesignationGroups,
  IGetDesignations
} from 'services/types/designationTypes';

export const useDesignations = ({
  organizationId,
  campaignId,
  isActiveFilter,
  refetchOnWindowFocus = 'always',
  augmentWithGroups = false
}: IGetDesignations) =>
  useQuery(
    [
      'getDesignations',
      { organizationId, campaignId, isActiveFilter, augmentWithGroups }
    ],
    () =>
      getDesignationsService({
        organizationId,
        campaignId,
        isActiveFilter,
        augmentWithGroups
      }),
    { refetchOnWindowFocus }
  );

export const useCreateDesignationMutation = (
  organizationId: string,
  queryOptions?: UseQueryOptions<Designation>
) =>
  useMutation(
    ['createDesignation', { organizationId }],
    (designationFormData: Designation) =>
      createDesignationService({
        organizationId,
        designationFormData
      }),
    queryOptions
  );

export const useUpdateDesignationMutation = (
  organizationId: string,
  designationId: string,
  queryOptions?: UseQueryOptions<Designation>
) =>
  useMutation(
    (designationFormData: Designation) =>
      updateDesignationService({
        organizationId,
        designationId,
        designationFormData
      }),
    queryOptions
  );

export const useDesignationGroups = (
  {
    organizationId,
    campaignId,
    refetchOnWindowFocus = 'always'
  }: IGetDesignationGroups,
  queryOptions?: UseQueryOptions<DesignationGroup[]>
) =>
  useQuery<DesignationGroup[]>(
    ['getDesignationGroups', { organizationId, campaignId }],
    () => getDesignationGroupsService({ organizationId, campaignId }),
    { refetchOnWindowFocus, ...queryOptions }
  );

export const useCreateDesignationGroupMutation = (
  organizationId: string,
  queryOptions?: UseQueryOptions<DesignationGroup>
) =>
  useMutation(
    ['createDesignationGroup', { organizationId }],
    (groupName: string) =>
      createDesignationGroupService({
        organizationId,
        groupName
      }),
    queryOptions
  );

export const useDeleteDesignationGroupByIdMutation = (
  organizationId: string,
  queryOptions?: UseQueryOptions<void>
) =>
  useMutation(
    ['deleteDesignationGroupById', { organizationId }],
    (designationGroupId: string) =>
      deleteDesignationGroupByIdService({
        organizationId,
        designationGroupId
      }),
    queryOptions
  );

export const useToggleDesignationStatusMutation = (
  organizationId: string,
  designationId: string,
  queryOptions?: UseQueryOptions<void, Error>
) =>
  useMutation(
    ['toggleDesignationStatus', { organizationId, designationId }],
    (isActive: boolean) =>
      toggleDesignationStatusService({
        organizationId,
        designationId,
        isActive
      }),
    queryOptions
  );
